import React from 'react';
import { Edit2, Trash2 } from 'lucide-react';

interface Coordinator {
  name: string;
  email: string;
}

interface Manager {
  name: string;
  email: string;
}

type ConsultantTableProps = {
  consultants: any[];
  onEdit: (consultant: any) => void;
  onDelete: (consultant: any) => void;
};

export const ConsultantTable = ({ consultants, onEdit, onDelete }: ConsultantTableProps) => {
  // Função para fazer parse dos coordenadores
  const parseCoordinators = (consultant: any): Coordinator[] => {
    const coordinators: Coordinator[] = [];

    // Verificar se existem dados do formato antigo
    

    // Verificar se existem dados do novo formato
    if (consultant.coordinators) {
      let coordinatorsData = consultant.coordinators;
      
      // Se é string JSON, fazer parse
      if (typeof coordinatorsData === 'string') {
        try {
          coordinatorsData = JSON.parse(coordinatorsData);
        } catch (error) {
          console.warn('Erro ao fazer parse dos coordenadores:', error);
          coordinatorsData = [];
        }
      }
      
      // Se agora é um array, adicionar aos coordenadores
      if (Array.isArray(coordinatorsData)) {
        coordinators.push(...coordinatorsData.map((coord: any) => ({
          name: coord.name || '',
          email: coord.email || coord.emails?.[0] || '' // Compatibilidade com formato anterior
        })));
      }
    }

    if (!coordinators.length && (consultant.coordinator || consultant.coordinator_mail)) {
      coordinators.push({
        name: consultant.coordinator || '',
        email: consultant.coordinator_mail || ''
      });
    }

    return coordinators;
  };

  // Função para fazer parse dos gerentes
  const parseManagers = (consultant: any): Manager[] => {
    const managers: Manager[] = [];

    

    // Verificar se existem dados do novo formato
    if (consultant.managers) {
      let managersData = consultant.managers;
      
      // Se é string JSON, fazer parse
      if (typeof managersData === 'string') {
        try {
          managersData = JSON.parse(managersData);
        } catch (error) {
          console.warn('Erro ao fazer parse dos gerentes:', error);
          managersData = [];
        }
      }
      
      // Se agora é um array, adicionar aos gerentes
      if (Array.isArray(managersData)) {
        managers.push(...managersData.map((manager: any) => ({
          name: manager.name || '',
          email: manager.email || manager.emails?.[0] || '' // Compatibilidade com formato anterior
        })));
      }
    }

    // Verificar se existem dados do formato antigo
    if (!managers.length && (consultant.manager || consultant.manager_mail)) {
      managers.push({
        name: consultant.manager || '',
        email: consultant.manager_mail || ''
      });
    }

    return managers;
  };

  // Função para formatar a exibição de coordenadores/gerentes
  const formatPersonList = (persons: (Coordinator | Manager)[]): string => {
    if (persons.length === 0) return '-';
    
    return persons
      .map(person => {
        if (person.name && person.email) {
          return `${person.name} (${person.email})`;
        } else if (person.name) {
          return person.name;
        } else if (person.email) {
          return person.email;
        }
        return '';
      })
      .filter(item => item !== '')
      .join(', ');
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Consultor
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              E-mail Consultor
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Coordenadores
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Gerentes
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Distribuidores
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ID Distribuidores
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {consultants.map((consultant) => {
            const coordinators = parseCoordinators(consultant);
            const managers = parseManagers(consultant);
            
            return (
              <tr key={consultant.guid} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {consultant.consultant}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {consultant.consultant_mail}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                  <div className="break-words">
                    {formatPersonList(coordinators)}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                  <div className="break-words">
                    {formatPersonList(managers)}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                  <div className="break-words">
                    {consultant.distributors?.map((dist: any) => dist.label).join(', ') || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {consultant.distributors?.map((dist: any) => dist.value).join(', ') || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onEdit(consultant)}
                      className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                      aria-label={`Editar consultor ${consultant.consultant}`}
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onDelete(consultant)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label={`Excluir consultor ${consultant.consultant}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};